import { useEffect } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import {
  type GetCurrentUserForSentryQuery,
  type GetCurrentUserForSentryQueryVariables,
} from '../generated/graphql';
import { useAuthContext } from '../contexts/AuthContext';
import { useLimitTrackingMobile } from '../hooks/useLimitTrackingMobile';
import { setSentryUser } from '../utils/logger';

const GET_CURRENT_USER_FOR_SENTRY = gql`
  query GetCurrentUserForSentry {
    getCurrentUserAndOrgs {
      user {
        id
      }
    }
  }
`;

/**
 * Identifies the current user in Sentry for error attribution, honoring the
 * user's `limit-tracking-mobile` privacy preference: when tracking is limited
 * the Sentry user is cleared so error reports stay anonymous (matching the
 * mobile app).
 *
 * The Sentry-owned counterpart to `initLogger` / `setSentryUser` in
 * `utils/logger.ts` — kept separate from analytics. Identification needs the
 * GraphQL client, so this renders nothing and must be mounted under
 * `ApolloClientProvider`.
 */
export const SentryUser = () => {
  const { tenantId } = useAuthContext();

  const { data, loading } = useQuery<
    GetCurrentUserForSentryQuery,
    GetCurrentUserForSentryQueryVariables
  >(GET_CURRENT_USER_FOR_SENTRY, {
    skip: !tenantId,
  });

  const userId = data?.getCurrentUserAndOrgs?.user?.id ?? undefined;
  const { limitTracking, loading: limitTrackingLoading } = useLimitTrackingMobile(userId);

  useEffect(() => {
    if (loading || limitTrackingLoading) {
      return;
    }

    setSentryUser(userId && !limitTracking ? { id: userId } : null);
  }, [loading, limitTrackingLoading, limitTracking, userId]);

  return null;
};
